import { useMemo } from 'react';

/**
 * A compact, light, planning-styled floating card that explains WHY a hovered
 * landmark is connected to its neighbors — WITHOUT requiring a click. For each
 * connected document it lists the shared themes (from the atlas
 * `relationships[].sharedTerms`), e.g.
 *
 *   Drainage Criteria Manual · shared: stormwater, detention
 *   Commercial Design Standards · shared: setbacks, grading
 *
 * Crisp / Linear. Pinned to a screen position near the cursor, clamped to stay
 * on-screen, and click-through (pointer-events: none) so it never blocks the map.
 */

export interface HoverConnection {
  id: number;
  title: string;
  /** Shared themes between the hovered doc and this one (may be empty). */
  sharedTerms: string[];
  /**
   * A written sentence explaining the connection (backend prose pass). When
   * present it is shown INSTEAD of the shared-term list (it reads clearer);
   * otherwise the card falls back to `sharedTerms`.
   */
  rationale?: string;
  weight: number;
}

interface HoverWhyCardProps {
  /** Title of the hovered landmark. */
  title: string;
  /** Optional district / type subtitle. */
  subtitle?: string | null;
  /** Connected documents, strongest first. */
  connections: HoverConnection[];
  /** Cursor position in viewport pixels (clientX / clientY). */
  x: number;
  y: number;
  /** Viewport size, used to clamp the card on-screen. */
  viewportWidth: number;
  viewportHeight: number;
}

// --- Tunable hover-card styling tokens (kept here, near the component) ---
const CARD = {
  width: 288, // px
  cursorOffsetX: 18, // px, card sits to the right of the cursor by default
  cursorOffsetY: 12,
  edgePad: 12, // keep this far from the viewport edge
  maxRows: 6, // cap the connected-doc list so the card stays compact
} as const;

export function HoverWhyCard({
  title,
  subtitle,
  connections,
  x,
  y,
  viewportWidth,
  viewportHeight,
}: HoverWhyCardProps) {
  const rows = connections.slice(0, CARD.maxRows);
  const extra = Math.max(0, connections.length - rows.length);

  // Clamp the card so it never spills off-screen; flip to the cursor's left
  // when there isn't room on the right.
  const { left, top } = useMemo(() => {
    let l = x + CARD.cursorOffsetX;
    if (l + CARD.width + CARD.edgePad > viewportWidth) {
      l = x - CARD.cursorOffsetX - CARD.width;
    }
    l = Math.max(CARD.edgePad, Math.min(l, viewportWidth - CARD.width - CARD.edgePad));
    // Rough height estimate to clamp vertically without measuring the DOM.
    const estHeight = 60 + rows.length * 30 + (extra > 0 ? 22 : 0);
    let t = y + CARD.cursorOffsetY;
    t = Math.max(CARD.edgePad, Math.min(t, viewportHeight - estHeight - CARD.edgePad));
    return { left: l, top: t };
  }, [x, y, viewportWidth, viewportHeight, rows.length, extra]);

  return (
    <div
      className="pointer-events-none fixed z-40 rounded-lg border border-slate-200 bg-white/95 backdrop-blur-sm"
      style={{
        left,
        top,
        width: CARD.width,
        boxShadow: '0 12px 32px -16px rgba(15,23,42,0.30)',
      }}
    >
      <div className="border-b border-slate-100 px-3.5 py-2.5">
        <div className="truncate text-[13px] font-semibold leading-tight text-slate-900">
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 truncate text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
            {subtitle}
          </div>
        )}
      </div>

      {rows.length > 0 ? (
        <ul className="px-3.5 py-2">
          {rows.map((c) => (
            <li key={c.id} className="py-[3px]">
              <div className="truncate text-[12px] font-medium leading-snug text-slate-700">
                {c.title}
              </div>
              {c.rationale ? (
                // Prefer the written sentence: it's clearer than a term list.
                <div className="text-[11px] leading-snug text-slate-500">
                  {c.rationale}
                </div>
              ) : (
                c.sharedTerms.length > 0 && (
                  <div className="truncate text-[11px] leading-snug text-slate-500">
                    <span className="text-slate-400">shared: </span>
                    {c.sharedTerms.join(', ')}
                  </div>
                )
              )}
            </li>
          ))}
          {extra > 0 && (
            <li className="pt-1 text-[11px] text-slate-400">
              +{extra} more connected document{extra === 1 ? '' : 's'}
            </li>
          )}
        </ul>
      ) : (
        <div className="px-3.5 py-2.5 text-[11px] text-slate-400">
          No mapped connections.
        </div>
      )}
    </div>
  );
}
